import React, { ReactNode, useRef, useState, useEffect } from "react";
import {
  PageCreatorSectionModel,
  OverlapType,
  PageCreatorSectionType,
} from "../Models/PageCreatorPage";

interface OverlappingWrapperProps {
  children: ReactNode;
  topBackgroundColor: string;
  bottomBackgroundColor: string;
  section: PageCreatorSectionModel | undefined;
}

export const getOverlapInfo = (
  section: PageCreatorSectionModel | undefined,
): { hasOverlap: boolean; overlapType?: OverlapType } => {
  if (!section) return { hasOverlap: false, overlapType: undefined };

  let hasOverlap = false;
  let overlapType;

  switch (section.sectionType) {
    case PageCreatorSectionType.Content:
      hasOverlap = section.content.hasOverlap;
      overlapType = section.content.overlapType;
      break;
    case PageCreatorSectionType.Feature:
      hasOverlap = section.feature.hasOverlap;
      overlapType = section.feature.overlapType;
      break;
    case PageCreatorSectionType.Form:
      hasOverlap = section.form.hasOverlap;
      overlapType = section.form.overlapType;
      break;
    default:
      break;
  }

  return { hasOverlap, overlapType };
};

export const getBackgroundColor = (
  section: PageCreatorSectionModel | undefined,
) => {
  if (!section) return "";

  switch (section.sectionType) {
    case PageCreatorSectionType.Hero:
      return section.hero.backgroundColor;
    case PageCreatorSectionType.Content:
      return section.content.backgroundColor;
    case PageCreatorSectionType.Feature:
      return section.feature.backgroundColor;
    case PageCreatorSectionType.Form:
      return section.form.backgroundColor;
    default:
      return "";
  }
};

const OverlappingWrapper = ({
  children,
  topBackgroundColor,
  bottomBackgroundColor,
  section,
}: OverlappingWrapperProps): JSX.Element => {
  const childrenRef = useRef<HTMLDivElement>(null);
  const [overlapAmount, setOverlapAmount] = useState<number>(0);
  const { hasOverlap, overlapType } = getOverlapInfo(section);

  useEffect(() => {
    const calculateOverlapAmount = () => {
      if (!childrenRef.current) return 0;

      const { clientHeight } = childrenRef.current;
      const isMobile = window.innerWidth < 768;
      const innerClientHeight = clientHeight - (isMobile ? 64 : 192);

      const calculateMobileOffset = () => (isMobile ? 32 : 96);

      const calculateOverlap = (factor: number) =>
        innerClientHeight * factor + calculateMobileOffset();

      switch (overlapType) {
        case OverlapType.OneThird:
          return calculateOverlap(1 / 3);
        case OverlapType.OneHalf:
          return clientHeight / 2;
        case OverlapType.TwoThirds:
          return calculateOverlap(2 / 3);
        default:
          return 0;
      }
    };

    if (!childrenRef.current) return;

    setOverlapAmount(calculateOverlapAmount());
  }, [overlapType]);

  return hasOverlap ? (
    <div id="overlapping-wrapper">
      <div
        id="top-background-color"
        style={{
          backgroundColor: topBackgroundColor,
          height: `${overlapAmount}px`,
          position: "relative",
        }}
      />
      <div
        id="negative-margin-wrapper"
        ref={childrenRef}
        style={{
          backgroundColor: bottomBackgroundColor || topBackgroundColor,
          marginTop: `-${overlapAmount}px`,
        }}
      >
        {children}
      </div>
    </div>
  ) : (
    <>{children}</>
  );
};

export default OverlappingWrapper;
